'use client';

import type { LinkProps, LinkRenderProps } from 'react-aria-components';
import React, { createContext, useContext, useMemo, type ReactElement, type ReactNode } from 'react';

export type RouterOptions = {
    /** Appends the current query string to the destination. */
    keepParams?: boolean;
    // If replace navigation is needed, add `replace?: boolean` here and pass it through renderLink to the router's Link.
};

type AriaLinkRender = NonNullable<LinkProps['render']>;
type LinkDOMProps = Parameters<AriaLinkRender>[0];

export type RenderRouterLink = (
    props: LinkDOMProps,
    renderProps: LinkRenderProps,
    routerOptions?: RouterOptions,
) => ReactElement;

export type RomaRouterProviderProps = {
    children: ReactNode;
    renderLink: RenderRouterLink;
    resolveHref?: (href: string, routerOptions?: RouterOptions) => string;
};

type RouterContextValue = Pick<RomaRouterProviderProps, 'renderLink' | 'resolveHref'>;

const RouterContext = createContext<RouterContextValue | null>(null);

export const RomaRouterProvider = ({ children, renderLink, resolveHref }: RomaRouterProviderProps) => {
    const context = useMemo(() => ({ renderLink, resolveHref }), [renderLink, resolveHref]);

    return <RouterContext.Provider value={context}>{children}</RouterContext.Provider>;
};

export const useRouterLink = (href: string | undefined, routerOptions?: RouterOptions) => {
    const context = useContext(RouterContext);
    const resolvedHref = href && context?.resolveHref ? context.resolveHref(href, routerOptions) : href;
    const render = context?.renderLink
        ? (props: LinkDOMProps, renderProps: LinkRenderProps) => context.renderLink(props, renderProps, routerOptions)
        : undefined;

    return { href: resolvedHref, render };
};
